import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/lib/i18n';
import { portfolioItems } from '@/data/portfolio';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Play } from 'lucide-react';

const types = ['All', 'Co-Produced', 'Produced'];

export default function PortfolioGrid({ limit }) {
  const { t } = useLanguage();
  const [selectedType, setSelectedType] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);

  const filtered = useMemo(() => {
    let items = portfolioItems;
    if (selectedType !== 'All') items = items.filter(i => i.type === selectedType);
    if (limit) items = items.slice(0, limit);
    return items;
  }, [selectedType, limit]);

  return (
    <div data-testid="portfolio-grid">
      {/* Filters */}
      {!limit && (
        <div className="mb-12 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="type-filters">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/20 self-center mr-2">Type</span>
            {types.map(tp => (
              <button
                key={tp}
                data-testid={`filter-type-${tp.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setSelectedType(tp)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${selectedType === tp
                  ? 'bg-[#00E5FF] text-black shadow-[0_0_20px_rgba(0,229,255,0.25)]'
                  : 'bg-white/[0.03] text-white/40 hover:text-white/70 hover:bg-white/[0.06] border border-white/[0.06]'
                  }`}
              >
                {tp === 'All' ? t('portfolio_filter_all') : tp}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filtered.map((item, i) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
            >
              <button
                data-testid={`portfolio-card-${item.id}`}
                onClick={() => setSelectedItem(item)}
                className="w-full text-left bg-[#0A0A0A] border border-[#1F1F1F] rounded-2xl overflow-hidden hover:border-[#7F00FF]/40 transition-all duration-300 group relative"
              >
                {/* Card gradient visual */}
                <div className="relative p-5 pb-0">

                  {/* Square Cover Box */}
                  <div className="aspect-square w-full rounded-xl overflow-hidden bg-black">

                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                  </div>

                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />

                  {/* Badges */}
                  <div className="absolute bottom-4 left-5 right-5">
                    <div className="flex gap-2">
                      <Badge className="bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/25 text-[10px] font-mono">
                        {item.type}
                      </Badge>
                    </div>
                  </div>

                  {/* Hover play icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    </div>
                  </div>

                </div>

                <div className="p-5">
                  <h3 className="font-syne font-bold text-lg mb-1.5 group-hover:text-[#7F00FF] transition-colors duration-200">
                    {item.title}
                  </h3>
                  <span className="text-sm text-white/40 mb-1 block">{Array.isArray(item.artist)
                    ? item.artist.join(" • ")
                    : item.artist}
                  </span>
                  <p className="text-xs text-white/25 font-mono">{item.year} &middot; {item.credits}</p>
                </div>
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-white/25 py-20 font-mono text-sm" data-testid="portfolio-no-results">
          {t('portfolio_no_results')}
        </p>
      )}

      {/* Modal */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent
          className="bg-[#0A0A0A] border-white/[0.08] max-w-lg"
          data-testid="portfolio-modal"
        >
          {selectedItem && (
            <>
              {selectedItem.spotifyEmbed && (
                <iframe
                  src={selectedItem.spotifyEmbed}
                  className="w-full h-[260px] mt-4 rounded-xl"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              )}


              <DialogHeader>
                <DialogTitle className="font-syne text-2xl" data-testid="modal-title">
                  {selectedItem.title}
                </DialogTitle>
                <DialogDescription className="text-sm text-white/40 mb-2" data-testid="modal-artist">
                  {Array.isArray(selectedItem.artist)
                    ? selectedItem.artist.join(" • ")
                    : selectedItem.artist}

                </DialogDescription>
                <DialogDescription className="text-white/40 font-mono text-xs">
                  {selectedItem.year} &middot; {selectedItem.type}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5 mt-4">
                <div>
                  <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/25 mb-2">
                    {t('modal_links')}
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(selectedItem.links).map(([platform, url]) => (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`modal-link-${platform}`}
                        className="flex items-center gap-1.5 text-sm text-[#00E5FF]/60 hover:text-[#00E5FF] transition-colors duration-200 capitalize"
                      >
                        {platform}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
